/**
 * The typed API client (ADR 0002): `hc<AppType>` gives compile-time request and response shapes
 * over plain JSON, and a custom `fetch` adds the bearer header plus the single refresh-and-retry
 * of ADR 0006. Types come from `@scrolivo/api` as a type-only import, so nothing from the
 * server bundle reaches the browser.
 */

import { hc } from "hono/client";
import type { AppType } from "@scrolivo/api";
import { authedFetch, type SubjectType } from "./auth-store.js";
import { ApiClientError, toClientError } from "./errors.js";
import { isOnline } from "./platform.js";

/**
 * Build spec 6.1: injected by Vite at build time. The localhost default is for development
 * only, and it is deliberately not a silent fallback anywhere else: a Cloudflare Pages build
 * that forgets `VITE_API_URL` would otherwise ship an app that loads, looks right and asks
 * localhost for every topic — a deploy that fails only on the child's phone, in the lesson.
 * On any other host we would rather the page say so at once (see `resolveApiUrl`).
 */
const LOCAL_API_URL = "http://localhost:8000";
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]", ""]);

export function resolveApiUrl(configured: string | undefined, host: string | undefined): string {
  if (configured) return configured;
  if (host === undefined || LOCAL_HOSTS.has(host)) return LOCAL_API_URL;
  throw new Error(
    "VITE_API_URL is not set, so this build has no API to talk to. Set it in the Cloudflare Pages project and redeploy.",
  );
}

export const API_URL: string = resolveApiUrl(
  import.meta.env["VITE_API_URL"] as string | undefined,
  typeof window === "undefined" ? undefined : window.location.hostname,
);

/**
 * ADR 0012: a sleeping Render Free instance takes about a minute to wake, so the first request of
 * the morning is slow, not failed. This was 25 s under Koyeb, which woke in about ten; at
 * 25 s a waking Render instance is aborted and reported to the child as "Keine Verbindung",
 * which is a lie about whose fault it is. The keep-warm ping should mean nobody ever waits
 * this long, but the timeout is what protects the child when the ping has missed.
 */
export const COLD_START_TIMEOUT_MS = 90_000;

function timeoutSignal(): AbortSignal {
  return AbortSignal.timeout(COLD_START_TIMEOUT_MS);
}

async function guard<T>(run: () => Promise<T>): Promise<T> {
  if (!isOnline()) throw new ApiClientError("offline", 0, "offline");
  try {
    return await run();
  } catch (err) {
    if (err instanceof ApiClientError) throw err;
    // A network failure, a DNS error or the cold-start timeout all land here.
    throw new ApiClientError("offline", 0, err instanceof Error ? err.message : "network error");
  }
}

/** The RPC client. Its `fetch` carries auth, the cold-start timeout and the error envelope. */
export const api = hc<AppType>(API_URL, {
  fetch: (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const path = url.startsWith(API_URL) ? url.slice(API_URL.length) : url;
    return guard(() => authedFetch(API_URL, path, { ...init, signal: timeoutSignal() }));
  },
});

/** For the few calls made before a session exists (login, the class tile grid). */
export async function publicJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  return guard(async () => {
    const response = await fetch(`${API_URL}${path}`, {
      ...init,
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...init.headers,
      },
      signal: timeoutSignal(),
    });
    if (!response.ok) throw await toClientError(response);
    return (await response.json()) as T;
  });
}

export async function authedJson<T>(
  path: string,
  init: RequestInit & { subjectType?: SubjectType } = {},
): Promise<T> {
  return guard(async () => {
    const response = await authedFetch(API_URL, path, { ...init, signal: timeoutSignal() });
    return (await response.json()) as T;
  });
}

export async function authedVoid(
  path: string,
  init: RequestInit & { subjectType?: SubjectType } = {},
): Promise<void> {
  await guard(() => authedFetch(API_URL, path, { ...init, signal: timeoutSignal() }));
}

/** ADR 0011: first-party error reporting, never a third-party crash reporter. Never throws. */
export function reportClientError(message: string, stack?: string): void {
  void fetch(`${API_URL}/client-error`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      message: message.slice(0, 500),
      ...(stack ? { stack: stack.slice(0, 4000) } : {}),
      url: window.location.pathname.slice(0, 500),
    }),
  }).catch(() => {
    // Reporting is best-effort; a failure here must never surface to a child.
  });
}
